import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { HelpTip } from "@/components/HelpTip";
import { UserCog, Plus, ShieldCheck, Mail, KeyRound, Clock } from "lucide-react";
import { StaffQuickLoginActions } from "@/components/StaffQuickLoginActions";
import { StaffLocations } from "@/components/StaffLocations";
import { StaffRegisters } from "@/components/StaffRegisters";
import { OneTimeCredentials } from "@/components/OneTimeCredentials";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { Link } from "react-router-dom";

const roleBadge = {
  owner: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  manager: "bg-violet-500/15 text-violet-500 border-violet-500/30",
  cashier: "bg-sky-500/15 text-sky-500 border-sky-500/30",
};
const blank = { name: "", email: "", password: "", role: "cashier", pin: "", active: true, invite: true, location_ids: [] };

export default function Staff() {
  const { business } = useAuth();
  const modules = useModules();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [pinDlg, setPinDlg] = useState(null);
  const [pinVal, setPinVal] = useState("");
  const [otc, setOtc] = useState(null);
  const [ent, setEnt] = useState(null);
  const [locations, setLocations] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [view, setView] = useState(() => (["active", "pending", "deactivated"].includes(new URLSearchParams(window.location.search).get("view")) ? new URLSearchParams(window.location.search).get("view") : "active"));
  // Views are pure filters over the existing fields: active (toggle) + has_password (activation). Nothing is redefined.
  const viewOf = (u) => (u.active === false ? "deactivated" : u.has_password ? "active" : "pending");
  const counts = users.reduce((a, u) => ({ ...a, [viewOf(u)]: (a[viewOf(u)] || 0) + 1 }), {});
  const VIEWS = [["active", "Active"], ["pending", "Pending Activation"], ["deactivated", "Deactivated"]];
  const shown = users.filter((u) => viewOf(u) === view);

  const load = () => { api.get("/users").then((r) => setUsers(r.data)); api.get("/entitlements/me").then((r) => setEnt(r.data)).catch(() => {}); api.get("/locations").then((r) => setLocations(r.data)).catch(() => setLocations([])); api.get("/pos/registers").then((r) => setRegisters(r.data.registers || [])).catch(() => setRegisters([])); };
  useEffect(() => { load(); }, []);
  const activeLocs = locations.filter((l) => l.status === "active");

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!form.invite && !form.password) { toast.error("Set a password or switch on Send secure invite"); return; }
    if (activeLocs.length > 0 && (form.location_ids || []).length === 0) { toast.error("Select at least one location"); return; }
    try {
      const payload = { ...form, password: form.invite ? undefined : form.password, location_ids: activeLocs.length ? form.location_ids : [] };
      const { data } = await api.post("/users", payload);
      toast.success(data.invited ? "Staff added. Activation invite sent" : "Staff member added");
      setOpen(false); setForm(blank); load();
      if (data.issued_credentials) setOtc({ creds: data.issued_credentials, name: data.name });
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.code === "staff_limit" ? d.message : apiError(d)); }
  };

  const [confirmDeact, setConfirmDeact] = useState(null);
  const setActive = async (u, active) => {
    try {
      await api.put(`/users/${u.id}`, { active }); load();
      toast.success(active ? `${u.name} restored to ${u.has_password ? "Active" : "Pending Activation"}` : `${u.name} deactivated — history kept, sessions signed out`);
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.code === "staff_limit" ? d.message : apiError(d)); }
  };
  const toggleActive = (u) => (u.active ? setConfirmDeact(u) : setActive(u, true));

  const sendInvite = async (u) => {
    try { await api.post(`/users/${u.id}/invite`); toast.success(`Activation invite sent to ${u.email}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const savePin = async () => {
    try {
      const { data } = await api.post(`/users/${pinDlg.id}/reset-pin`, {});
      toast.success("Authorization PIN reset"); const nm = pinDlg.name; setPinDlg(null); setPinVal(""); load();
      if (data.issued_credentials) setOtc({ creds: data.issued_credentials, name: nm });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><UserCog className="w-7 h-7" /> Staff & Roles</h1>
          <p className="text-muted-foreground">Invite employees securely. They set their own password. Managers get an approval PIN. <Link to="/help?topic=assign-staff-location" className="text-emerald-600 hover:underline text-sm" data-testid="help-link-staff">Learn about staff assignments</Link></p>
          {ent?.staff && <p className="text-xs text-muted-foreground mt-1" data-testid="staff-usage-indicator">{activeLocs.length ? `Up to ${ent.staff.limit} active staff per location` : `${ent.staff.used} of ${ent.staff.limit} staff accounts used`}{activeLocs.length ? " · " + activeLocs.map((l) => `${l.name} ${l.staff_used}/${l.staff_limit}`).join(" · ") : ""}</p>}
        </div>
        <Button onClick={() => { setForm(blank); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Staff
        </Button>
      </div>

      <AlertDialog open={!!confirmDeact} onOpenChange={(o) => !o && setConfirmDeact(null)}>
        <AlertDialogContent data-testid="deactivate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {confirmDeact?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They are signed out everywhere immediately and can no longer log in or use Quick Login. Their record, sales, shifts and history are kept. You can restore them from the Deactivated view at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => { const u = confirmDeact; setConfirmDeact(null); setActive(u, false); }} data-testid="deactivate-confirm-btn">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex gap-1 border-b border-border overflow-x-auto" role="tablist" data-testid="staff-views">
        {VIEWS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={view === k} className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${view === k ? "border-emerald-500 font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`} onClick={() => { setView(k); window.history.replaceState(null, "", k === "active" ? "/staff" : `/staff?view=${k}`); }} data-testid={`staff-view-${k}`}>{l} <span className="ml-1 text-xs text-muted-foreground" data-testid={`staff-view-count-${k}`}>{counts[k] || 0}</span></button>)}
      </div>
      {shown.length === 0 && <p className="text-sm text-muted-foreground" data-testid="staff-view-empty">{view === "active" ? "No active staff." : view === "pending" ? "No staff waiting to activate. New invites appear here until the employee sets a password." : "No deactivated staff. Deactivated people keep their history and can be restored here."}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((u) => (
          <Card key={u.id} className="p-5" data-testid={`staff-card-${u.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center font-head font-bold">{u.name[0]}</div>
                <div>
                  <p className="font-head font-bold">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                </div>
              </div>
              <Badge className={`${roleBadge[u.role]} capitalize`}>{u.role}</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {u.has_pin && <Badge className="bg-violet-500/10 text-violet-500 border-violet-500/20 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />PIN set</Badge>}
              {!u.has_password && <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-[10px]"><Clock className="w-3 h-3 mr-1" />Pending activation</Badge>}
              {u.active === false && <Badge className="bg-rose-500/10 text-rose-600 border-rose-500/20 text-[10px]" data-testid={`deactivated-badge-${u.id}`}>Deactivated · history kept</Badge>}
            </div>
            {u.role !== "owner" && <StaffLocations u={u} locations={locations} onChanged={load} />}
            {u.role !== "owner" && <StaffRegisters u={u} registers={registers} onChanged={load} />}
            {u.role !== "owner" && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-border">
                {!u.has_password && (
                  <Button size="sm" variant="outline" onClick={() => sendInvite(u)} data-testid={`invite-${u.id}`}><Mail className="w-3.5 h-3.5 mr-1.5" /> Resend Invite</Button>
                )}
                {u.role !== "cashier" && <Button size="sm" variant="outline" onClick={() => { setPinDlg(u); setPinVal(""); }} data-testid={`resetpin-${u.id}`}><KeyRound className="w-3.5 h-3.5 mr-1.5" /> {u.has_pin ? "Reset Authorization PIN" : "Issue Authorization PIN"}</Button>}
                <StaffQuickLoginActions u={u} badgeFeature={modules.isEnabled("staff_badge_login")} onChanged={load} onIssued={(creds) => setOtc({ creds, name: u.name })} />
                <div className="flex items-center gap-2 text-xs ml-auto">
                  <span className="text-muted-foreground">{u.active ? "Active" : "Deactivated"}</span>
                  <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-${u.id}`} />
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="staff-form">
          <DialogHeader><DialogTitle className="font-head">Add Staff Member</DialogTitle>
            <DialogDescription>Invite an employee securely or set a temporary password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sf-name" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sf-email" /></div>
            <div>
              <Label className="text-xs">Role</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="sf-role">
                <option value="cashier">Cashier</option><option value="manager">Manager</option>
              </select>
            </div>
            {activeLocs.length > 0 && (
              <div data-testid="sf-locations">
                <Label className="text-xs">Locations (one account, one seat per location)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {activeLocs.map((l) => { const on = (form.location_ids || []).includes(l.id); return (
                    <button type="button" key={l.id} onClick={() => setForm({ ...form, location_ids: on ? form.location_ids.filter((x) => x !== l.id) : [...(form.location_ids || []), l.id] })}
                      className={`text-xs rounded-full border px-3 py-1 ${on ? "bg-emerald-600 text-white border-emerald-600" : "border-border"} ${l.staff_at_limit && !on ? "opacity-60" : ""}`} data-testid={`sf-loc-${l.id}`}>
                      {l.name} · {l.staff_used}/{l.staff_limit}{l.staff_at_limit ? " (full)" : ""}</button>); })}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div><p className="text-sm font-medium flex items-center">Send secure invite <HelpTip testid="sf-invite-help" text="The employee gets an email with a single-use link to set their own password. You never see or set it." /></p><p className="text-xs text-muted-foreground">Recommended: they set their own password</p></div>
              <Switch checked={form.invite} onCheckedChange={(v) => setForm({ ...form, invite: v })} data-testid="sf-invite" />
            </div>
            {!form.invite && (
              <div><Label className="text-xs">Temporary Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sf-password" /></div>
            )}
            <p className="text-[11px] text-muted-foreground" data-testid="sf-pin-note">PINs are generated by NEEC and shown once after saving: a 4-digit Login PIN{form.role === "manager" ? " and a 6-digit Authorization PIN for approvals" : ""}.</p>
            <Button onClick={save} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-staff">
              {form.invite ? <><Mail className="w-4 h-4 mr-1.5" /> Add & Send Invite</> : "Add Staff"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinDlg} onOpenChange={(o) => { if (!o) setPinDlg(null); }}>
        <DialogContent className="max-w-xs" data-testid="pin-reset-form">
          <DialogHeader><DialogTitle className="font-head">{pinDlg?.has_pin ? "Reset" : "Set"} PIN · {pinDlg?.name}</DialogTitle>
            <DialogDescription>NEEC will generate a new 6-digit Authorization PIN and show it once. The previous PIN stops working immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="hidden"><Input value={pinVal} onChange={(e) => setPinVal(e.target.value.replace(/\D/g, ""))} maxLength={6} className="mono text-lg" data-testid="pin-reset-value" /></div>
            <Button onClick={savePin} className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="pin-reset-submit">Generate new Authorization PIN</Button>
          </div>
        </DialogContent>
      </Dialog>
      <OneTimeCredentials creds={otc?.creds} staffName={otc?.name} onClose={() => setOtc(null)} />
    </div>
  );
}
